import type { JSONContent } from "@tiptap/core";

export const TABLE_SCHEMA_VERSION = 1 as const;
export const TABLE_DEFAULT_ROWS = 3;
export const TABLE_DEFAULT_COLS = 3;
export const TABLE_MAX_ROWS = 50;
export const TABLE_MAX_COLS = 20;

export type TableBlockData = {
  schemaVersion: typeof TABLE_SCHEMA_VERSION;
  doc: JSONContent;
  caption?: string;
  variant?: "default" | "compact";
  fixedRow?: number | null;
  fixedCol?: number | null;
};

export type TableStats = {
  tableCount: number;
  rows: number;
  cols: number;
  headerRows: number;
  headerCols: number;
};

export type TableInitOptions = {
  rows?: number;
  cols?: number;
  headerRow?: boolean;
  headerColumn?: boolean;
};

const ALLOWED_NODES = new Set([
  "doc",
  "table",
  "tableRow",
  "tableCell",
  "tableHeader",
  "paragraph",
  "text",
]);

const clampNumber = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const cloneJson = <T,>(value: T): T => {
  const structured = (globalThis as any)?.structuredClone;
  if (typeof structured === "function") {
    try {
      return structured(value);
    } catch {
      // ignore clone failures
    }
  }
  return JSON.parse(JSON.stringify(value));
};

function makeText(text = ""): JSONContent {
  return { type: "text", text };
}

function makeParagraph(text = ""): JSONContent {
  return { type: "paragraph", content: text ? [makeText(text)] : [] };
}

function makeCell(isHeader: boolean, text = ""): JSONContent {
  return {
    type: isHeader ? "tableHeader" : "tableCell",
    content: [makeParagraph(text)],
  };
}

export function createTableDoc(options: TableInitOptions = {}): JSONContent {
  const rows = clampNumber(
    Math.round(options.rows ?? TABLE_DEFAULT_ROWS),
    1,
    TABLE_MAX_ROWS,
  );
  const cols = clampNumber(
    Math.round(options.cols ?? TABLE_DEFAULT_COLS),
    1,
    TABLE_MAX_COLS,
  );
  const headerRow = options.headerRow !== false;
  const headerColumn = options.headerColumn === true;

  const content = Array.from({ length: rows }, (_, rowIndex) => {
    const isHeaderRow = headerRow && rowIndex === 0;
    return {
      type: "tableRow",
      content: Array.from({ length: cols }, (_, colIndex) => {
        const isHeaderCell =
          isHeaderRow || (headerColumn && colIndex === 0) || false;
        return makeCell(isHeaderCell);
      }),
    };
  });

  return {
    type: "doc",
    content: [
      {
        type: "table",
        content,
      },
    ],
  };
}

function sanitizeParagraph(node: any): JSONContent {
  const content = Array.isArray(node?.content)
    ? (node.content as JSONContent[])
    : [];
  const textNodes = content
    .filter((child) => child?.type === "text")
    .map((child) => makeText(typeof child?.text === "string" ? child.text : ""));
  return { type: "paragraph", content: textNodes };
}

function sanitizeCell(node: any, isHeaderAllowed: boolean): JSONContent {
  const type = node?.type;
  if (type !== "tableCell" && type !== "tableHeader") {
    throw new Error("Invalid table cell type");
  }
  if (!isHeaderAllowed && type === "tableHeader") {
    throw new Error("Header cells are not allowed here");
  }
  const paragraphs = Array.isArray(node?.content)
    ? node.content.map((child: JSONContent) => {
        if (child?.type !== "paragraph") {
          throw new Error("Only paragraphs are allowed inside cells");
        }
        return sanitizeParagraph(child);
      })
    : [];
  return {
    type,
    attrs: sanitizeCellAttrs(node?.attrs),
    content: paragraphs.length ? paragraphs : [makeParagraph()],
  };
}

function sanitizeCellAttrs(attrs: any): JSONContent["attrs"] {
  const colspan =
    Number.isInteger(attrs?.colspan) && attrs.colspan > 0
      ? Math.min(attrs.colspan, TABLE_MAX_COLS)
      : 1;
  const rowspan =
    Number.isInteger(attrs?.rowspan) && attrs.rowspan > 0
      ? Math.min(attrs.rowspan, TABLE_MAX_ROWS)
      : 1;
  const colwidth = Array.isArray(attrs?.colwidth)
    ? attrs.colwidth
        .slice(0, colspan)
        .map((width: unknown) =>
          typeof width === "number" && Number.isFinite(width) && width > 0
            ? Math.round(width)
            : null,
        )
    : null;

  return {
    colspan,
    rowspan,
    colwidth: colwidth?.some((width: number | null) => typeof width === "number")
      ? colwidth
      : null,
    bold: attrs?.bold === true,
    italic: attrs?.italic === true,
    textAlign:
      attrs?.textAlign === "center" || attrs?.textAlign === "right"
        ? attrs.textAlign
        : "left",
    direction: attrs?.direction === "rtl" ? "rtl" : "ltr",
    raw: typeof attrs?.raw === "string" ? attrs.raw : null,
    computed: typeof attrs?.computed === "string" ? attrs.computed : null,
  };
}

function assertAllowed(node: any) {
  if (!ALLOWED_NODES.has(node?.type)) {
    throw new Error(`Unsupported node type "${node?.type ?? "unknown"}"`);
  }
}

function findFirstTable(node: JSONContent): {
  table: JSONContent | null;
  count: number;
} {
  let table: JSONContent | null = null;
  let count = 0;
  const walk = (n: JSONContent) => {
    if (!n) return;
    assertAllowed(n);
    if (n.type === "table") {
      count += 1;
      if (!table) table = n;
    }
    (n.content ?? []).forEach((child: any) => walk(child));
  };
  walk(node);
  return { table, count };
}

export function sanitizeTableDoc(
  doc: JSONContent,
  options: { maxRows?: number; maxCols?: number } = {},
): { doc: JSONContent; stats: TableStats; clamped: boolean } {
  const maxRows = clampNumber(options.maxRows ?? TABLE_MAX_ROWS, 1, 500);
  const maxCols = clampNumber(options.maxCols ?? TABLE_MAX_COLS, 1, 500);
  const clone = cloneJson(doc);

  if (clone?.type !== "doc" || !Array.isArray(clone.content)) {
    throw new Error("Table document must be a ProseMirror doc node");
  }

  const { table, count } = findFirstTable(clone);
  if (!table || count !== 1) {
    throw new Error("Table document must contain exactly one table node");
  }
  if (!Array.isArray(table.content) || table.content.length === 0) {
    throw new Error("Table must have at least one row");
  }

  let clamped = false;
  if (table.content.length > maxRows) {
    clamped = true;
  }
  table.content = table.content.slice(0, maxRows).map((row: any) => {
    if (row?.type !== "tableRow") {
      throw new Error("All table children must be table rows");
    }
    const cells = Array.isArray(row?.content) ? row.content : [];
    if (!cells.length) {
      throw new Error("Table rows must have at least one cell");
    }
    if (cells.length > maxCols) clamped = true;
    const safeCells = cells.slice(0, maxCols).map((cell: any) =>
      sanitizeCell(cell, true),
    );
    return { type: "tableRow", content: safeCells };
  });

  const stats = getTableStats(clone);
  if (stats.rows > maxRows || stats.cols > maxCols) {
    clamped = true;
  }

  return { doc: clone, stats, clamped };
}

export function getTableStats(doc: JSONContent): TableStats {
  const { table, count } = findFirstTable(doc);
  if (!table || count !== 1) {
    throw new Error("Table document must contain exactly one table node");
  }

  const tableContent = Array.isArray(table.content) ? table.content : null;
  const rows = tableContent?.length ?? 0;
  if (!tableContent || rows === 0) {
    throw new Error("Table must have at least one row");
  }

  let cols = 0;
  let headerRows = 0;
  let headerCols = 0;

  tableContent.forEach((row: JSONContent, rowIndex: number) => {
    assertAllowed(row);
    const cells = Array.isArray(row?.content) ? row.content : [];
    if (cells.length === 0) {
      throw new Error("Table rows must have at least one cell");
    }
    cols = Math.max(cols, cells.length);

    const isHeaderRow = cells.every(
      (cell: JSONContent) => cell?.type === "tableHeader",
    );
    if (isHeaderRow) headerRows += 1;

    cells.forEach((cell: JSONContent, cellIndex: number) => {
      assertAllowed(cell);
      const paragraphs = Array.isArray(cell?.content) ? cell.content : [];
      if (!paragraphs.length) {
        throw new Error("Cells must contain at least one paragraph");
      }
      if (cellIndex === 0 && cell?.type === "tableHeader") {
        headerCols = 1;
      }
      paragraphs.forEach((para: JSONContent) => {
        assertAllowed(para);
        const leafs = Array.isArray(para?.content) ? para.content : [];
        leafs.forEach((leaf: JSONContent) => {
          assertAllowed(leaf);
        });
      });
    });
  });

  return { tableCount: count, rows, cols, headerRows, headerCols };
}
